(() => {
  const root = document.documentElement;
  const money = (cents) => {
    const currency = window.Shopify?.currency?.active || 'EUR';
    try { return new Intl.NumberFormat(root.lang || 'de-DE', { style: 'currency', currency }).format(cents / 100); }
    catch (_) { return `${(cents / 100).toFixed(2).replace('.', ',')} €`; }
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));

  const closeAll = (header) => {
    header.querySelectorAll('.is-open').forEach(element => element.classList.remove('is-open'));
    header.querySelectorAll('[aria-hidden="false"]').forEach(element => element.setAttribute('aria-hidden', 'true'));
    const scrim = header.querySelector('[data-w-scrim]');
    if (scrim) scrim.hidden = true;
    document.body.style.overflow = '';
  };

  const openLayer = (header, layer) => {
    closeAll(header);
    if (!layer) return;
    layer.classList.add('is-open');
    layer.setAttribute('aria-hidden', 'false');
    const scrim = header.querySelector('[data-w-scrim]');
    if (scrim) scrim.hidden = false;
    document.body.style.overflow = 'hidden';
  };

  const renderCart = (header, cart) => {
    const body = header.querySelector('[data-w-cart-body]');
    const countBadges = document.querySelectorAll('[data-w-cart-count]');
    countBadges.forEach(badge => {
      badge.textContent = cart.item_count;
      badge.hidden = cart.item_count === 0;
    });
    if (!body) return;

    if (!cart.items.length) {
      body.innerHTML = '<div class="w-empty-cart"><span><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M5 8h14l-1 13H6L5 8Z"/><path d="M9 9V6a3 3 0 0 1 6 0v3"/></svg></span><h3>Dein Warenkorb ist leer</h3><p>Entdecke ausgewählte Produkte für ein schöneres Zuhause.</p><a class="w-button w-button--dark" href="/collections/all">Produkte entdecken</a></div>';
      return;
    }

    const threshold = Number(header.querySelector('[data-w-cart-drawer]')?.dataset.freeShipping || 4900);
    const remaining = Math.max(0, threshold - cart.total_price);
    const progress = threshold > 0 ? Math.min(100, cart.total_price / threshold * 100) : 100;
    const shippingMessage = remaining === 0 ? 'Kostenloser Versand freigeschaltet!' : `Noch ${money(remaining)} bis zum kostenlosen Versand`;
    const items = cart.items.map(item => `
      <div class="w-cart-line">
        <img src="${escapeHtml(item.image || '')}" alt="${escapeHtml(item.product_title)}" width="180" height="210">
        <div class="w-cart-line__copy">
          <h3>${escapeHtml(item.product_title)}</h3>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<small>${escapeHtml(item.variant_title)}</small>` : ''}
          <strong>${item.quantity} × ${money(item.final_price)}</strong>
        </div>
      </div>`).join('');

    body.innerHTML = `
      <div class="w-cart-shipping"><div class="w-cart-shipping__bar"><span style="width:${progress}%"></span></div><p>${shippingMessage}</p></div>
      <div class="w-cart-items">${items}</div>
      <div class="w-cart-summary"><div><span>Zwischensumme</span><strong>${money(cart.total_price)}</strong></div><p>Steuern inklusive. Versand wird beim Checkout berechnet.</p><a class="w-button w-button--dark" href="/checkout">Sicher zur Kasse</a></div>`;
  };

  const refreshCart = async (header, shouldOpen = false) => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart request failed');
    const cart = await response.json();
    renderCart(header, cart);
    if (shouldOpen) openLayer(header, header.querySelector('[data-w-cart-drawer]'));
    return cart;
  };

  const initHeader = (header) => {
    if (!header || header.dataset.wReady === 'true') return;
    header.dataset.wReady = 'true';
    const mobileMenu = header.querySelector('[data-w-mobile-menu]');
    const searchPanel = header.querySelector('[data-w-search-panel]');
    const cartDrawer = header.querySelector('[data-w-cart-drawer]');
    header.querySelector('[data-w-menu-open]')?.addEventListener('click', () => openLayer(header, mobileMenu));
    header.querySelector('[data-w-search-open]')?.addEventListener('click', () => {
      openLayer(header, searchPanel);
      setTimeout(() => searchPanel?.querySelector('input[type="search"]')?.focus(), 80);
    });
    header.querySelector('[data-w-cart-open]')?.addEventListener('click', () => refreshCart(header, true).catch(() => openLayer(header, cartDrawer)));
    header.querySelectorAll('[data-w-menu-close], [data-w-search-close], [data-w-cart-close], [data-w-scrim]').forEach(button => button.addEventListener('click', () => closeAll(header)));
    document.addEventListener('keydown', event => { if (event.key === 'Escape') closeAll(header); });
  };

  const initProduct = (section) => {
    if (!section || section.dataset.wReady === 'true') return;
    section.dataset.wReady = 'true';
    const mainImage = section.querySelector('[data-w-main-image]');
    section.querySelectorAll('[data-w-thumb]').forEach(thumb => thumb.addEventListener('click', () => {
      section.querySelectorAll('[data-w-thumb]').forEach(item => item.classList.remove('is-active'));
      thumb.classList.add('is-active');
      if (mainImage) { mainImage.src = thumb.dataset.image; mainImage.srcset = ''; mainImage.alt = thumb.dataset.alt || ''; }
    }));

    const variantInput = section.querySelector('[data-w-variant-input]');
    const price = section.querySelector('[data-w-price]');
    const mobilePrice = section.querySelector('[data-w-mobile-price]');
    const compare = section.querySelector('[data-w-compare]');
    const saving = section.querySelector('[data-w-saving]');
    const title = section.querySelector('[data-w-variant-title]');
    const stock = section.querySelector('[data-w-stock]');
    const addButton = section.querySelector('[data-w-add-button]');
    const mobileAdd = section.querySelector('[data-w-mobile-add]');

    section.querySelectorAll('[data-w-variant]').forEach(button => button.addEventListener('click', () => {
      if (button.disabled) return;
      section.querySelectorAll('[data-w-variant]').forEach(item => item.classList.remove('is-active'));
      button.classList.add('is-active');
      if (variantInput) variantInput.value = button.dataset.id;
      if (title) title.textContent = button.dataset.title;
      if (price) price.textContent = button.dataset.price;
      if (mobilePrice) mobilePrice.textContent = button.dataset.price;
      if (compare) { compare.textContent = button.dataset.compare || ''; compare.hidden = !button.dataset.compare; }
      if (saving) { saving.textContent = button.dataset.saving ? `Du sparst ${button.dataset.saving}` : ''; saving.hidden = !button.dataset.saving; }
      const available = button.dataset.available === 'true';
      if (stock) { stock.classList.toggle('is-sold-out', !available); stock.querySelector('span').textContent = available ? 'Auf Lager – versandbereit' : 'Derzeit nicht verfügbar'; }
      if (addButton) { addButton.disabled = !available; addButton.querySelector('span').textContent = available ? 'In den Warenkorb' : 'Ausverkauft'; }
      if (mobileAdd) { mobileAdd.disabled = !available; mobileAdd.textContent = available ? 'In den Warenkorb' : 'Ausverkauft'; }
      const url = new URL(window.location.href); url.searchParams.set('variant', button.dataset.id); window.history.replaceState({}, '', url);
    }));

    const quantity = section.querySelector('[data-w-quantity]');
    section.querySelector('[data-w-quantity-minus]')?.addEventListener('click', () => { if (quantity) quantity.value = Math.max(1, Number(quantity.value || 1) - 1); });
    section.querySelector('[data-w-quantity-plus]')?.addEventListener('click', () => { if (quantity) quantity.value = Number(quantity.value || 1) + 1; });
  };

  const bindAddForms = () => {
    document.querySelectorAll('[data-wohnish-add-form]').forEach(form => {
      if (form.dataset.wReady === 'true') return;
      form.dataset.wReady = 'true';
      form.addEventListener('submit', async event => {
        const header = document.querySelector('[data-w-header]');
        if (!header || !window.fetch) return;
        event.preventDefault();
        const submit = form.querySelector('[type="submit"]');
        submit?.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          if (!response.ok) throw new Error('Add to cart failed');
          await refreshCart(header, typeof window.upcartOpenCart !== 'function');
        } catch (_) {
          form.submit();
        } finally {
          submit?.removeAttribute('aria-busy');
        }
      });
    });
  };

  const initRecommendations = () => {
    document.querySelectorAll('product-recommendations[data-url]').forEach(section => {
      if (section.dataset.loaded === 'true') return;
      section.dataset.loaded = 'true';
      fetch(section.dataset.url)
        .then(response => response.text())
        .then(text => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const replacement = html.querySelector(`#${CSS.escape(section.id)}`);
          if (replacement?.innerHTML.trim()) { section.innerHTML = replacement.innerHTML; bindAddForms(); }
        })
        .catch(() => {});
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll?.('[data-w-header]').forEach(initHeader);
    scope.querySelectorAll?.('[data-w-product-section]').forEach(initProduct);
    bindAddForms();
    initRecommendations();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
